import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import useUserStore from "@store/useUserStore";
import { useSnackbar } from "contexts/SnackbarContext";
import LoadingSpinner from "components/common/LoadingSpiner";
import NoData from "components/common/NoData";
import { getClassProduct, getLocationList, getSportsTypes } from "api/class";
import {
    createLessonRequest,
    fetchLessonRequestOptions,
    getLessonRequestErrorMessage,
} from "api/lessonRequest";
import {
    LESSON_REQUEST_TYPE_ORDER,
    LESSON_REQUEST_TYPES,
    TIME_SLOT_GROUPS,
    getDefaultDurationMinutes,
    getRequestTypeMeta,
} from "constants/lessonRequest";

import Step1RequestType from "components/lessonRequest/Step1RequestType";
import Step2Schedule from "components/lessonRequest/Step2Schedule";
import Step3Details from "components/lessonRequest/Step3Details";
import Step4Confirm from "components/lessonRequest/Step4Confirm";

import "./LessonRequest.scss";
import SubHeader from "../../components/common/SubHeader";
import { scrollToTop } from "utils/scrollToTop";

// --- Helper Functions ---
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const addMinutes = (time, minutes) => dayjs(`2026-01-01 ${time}`).add(minutes, "minute").format("HH:mm");
const toMinutes = (time) => {
    const [hour, minute] = String(time).split(":").map(Number);
    return (hour * 60) + (minute || 0);
};
const fitsSameDayDuration = (time, durationMinutes) => toMinutes(time) + durationMinutes < 24 * 60;


const formatLocationSub = (location) => location?.address || location?.region || location?.description || "서울 송파 · 수심 2~5m";

const normalizeArray = (payload, key) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.[key])) return payload[key];
    return [];
};

const getLocationLabel = (location) => location?.name || "장소 이름 없음";

const normalizeText = (value) => String(value || "").toLowerCase().replace(/\s+/g, "");

const getLocationImage = (location) => (
    location?.thumbnail_image ||
    location?.thumbnail ||
    location?.image_url ||
    location?.image ||
    location?.main_image ||
    location?.photo_url ||
    null
);

const getLocationType = (location) => {
    const raw = normalizeText(location?.location_type || location?.type || location?.category || location?.category_name || "");
    if (raw.includes("개방") || raw.includes("openwater")) return "개방수역";
    if (raw.includes("잠수풀") || raw.includes("deep")) return "잠수풀";
    if (raw.includes("수영장") || raw.includes("swimming")) return "수영장";

    const haystack = normalizeText(`${location?.name || ""} ${location?.address || ""} ${location?.description || ""}`);
    if (haystack.includes("개방") || haystack.includes("바다") || haystack.includes("해양") || haystack.includes("openwater")) return "개방수역";
    if (haystack.includes("잠수풀") || haystack.includes("풀장") || haystack.includes("k26") || haystack.includes("딥")) return "잠수풀";
    if (haystack.includes("수영장") || haystack.includes("swimming")) return "수영장";
    return "잠수풀";
};

// 체험(TRIAL)은 더 이상 sport_name 자동 매칭으로 상품을 고르지 않는다(요구사항: 자동 fallback 제거).
// 브랜드별 지정 체험 상품은 BE 가 home brand 기준으로 서버측 resolve 하며, 미설정 시 생성 요청을 400 으로
// 차단한다(FE 는 lesson_product_id 를 보내지 않고 brand_id 만 보낸다).

const toPositiveInt = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

// 강습권의 "원래 총 회차". 잔여(remaining 등)는 탭 계산에 절대 쓰지 않는다.
const getTicketTotalSessions = (ticket) =>
    toPositiveInt(
        ticket?.total ||
        ticket?.sessions_total ||
        ticket?.total_sessions ||
        ticket?.count ||
        ticket?.sessions_count
    );

const getProductSessionsCount = (product) =>
    toPositiveInt(
        product?.sessions_count ||
        product?.sessions_total ||
        product?.total_sessions ||
        product?.count
    );

// 세션 탭 수는 연결 상품(ticket.lesson_product_id로 매칭)의 sessions_count를 최우선으로 쓴다.
// 강습권 총 회차는 상품 회차가 없을 때만 하위호환 폴백으로 쓰고, 둘 다 없으면 기본 3.
const getSessionSlotCount = (product, ticket) =>
    getProductSessionsCount(product) || getTicketTotalSessions(ticket) || 3;

const getProductTheoryDay = (product) => {
    const day = Number(product?.theory_required_before_day || product?.theory_required_day || product?.theory_day);
    return Number.isFinite(day) && day > 0 ? day : null;
};

const productHasTheory = (product) => (
    product?.has_theory === true ||
    product?.is_theory === true ||
    product?.theory_included === true ||
    product?.includes_theory === true ||
    Boolean(getProductTheoryDay(product))
);

// 강습권(ticket) 레벨 이론 메타. 연결 상품 메타가 이론을 누락/불완전하게 줄 때 폴백.
const getTicketTheoryDay = (ticket) => {
    const day = Number(ticket?.theory_required_before_day || ticket?.theory_required_day || ticket?.theory_day);
    return Number.isFinite(day) && day > 0 ? day : null;
};

const ticketHasTheory = (ticket) => (
    ticket?.has_theory === true ||
    ticket?.is_theory === true ||
    ticket?.theory_included === true ||
    ticket?.includes_theory === true ||
    Boolean(getTicketTheoryDay(ticket))
);

const getTicketLabel = (ticket) =>
    ticket?.lesson_product_name || ticket?.label || `수강권 #${ticket?.ticket_id || ticket?.id}`;

// 강습권 잔여 회차. 표시 전용.
const getTicketRemaining = (ticket) => {
    const value =
        ticket?.remaining ??
        ticket?.remain ??
        ticket?.sessions_remaining ??
        ticket?.remaining_sessions;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

// 카드 라벨 "잔여 N회 / 총 M회". 총 회차는 연결 상품 sessions_count 우선, 없으면 강습권 총 회차.
// 둘 다 없으면 "/ M회"를 생략한다.
const getTicketSessionsLabel = (ticket, product) => {
    const remaining = getTicketRemaining(ticket);
    const total = getProductSessionsCount(product) || getTicketTotalSessions(ticket);
    const remainingText = remaining ?? "-";
    return total ? `잔여 ${remainingText}회 / ${total}회` : `잔여 ${remainingText}회`;
};

// 스페셜 트레이닝 요청은 GENERAL_1T10 트레이닝/특강 티켓만 허용한다.
// 기존 특강권(SPECIAL_1/11/111), 체험권(ONE_DAY), 일반 강습권(NONE)은 스페셜 트레이닝 신청에 사용할 수 없다.
const SPECIAL_TRAINING_TICKET_TYPE = "GENERAL_1T10";

// --- Main Component ---

export default function LessonRequestCompose() {
    const navigate = useNavigate();
    const { type: routeType } = useParams();
    const userInfo = useUserStore((state) => state.userInfo);
    // 브랜드/회사는 홈에서 이미 선택된다. no-ticket 결제 payload는 홈 브랜드를 우선 사용한다.
    const homeBrand = useUserStore((state) => state.homeBrand);
    const homeBrandId = homeBrand?.home_brand_id ?? homeBrand?.brand_id ?? homeBrand?.id ?? null;
    const { showSuccess, showError } = useSnackbar();

    const initialType = LESSON_REQUEST_TYPE_ORDER.includes(routeType?.toUpperCase())
        ? routeType.toUpperCase()
        : null;

    // routeType으로 진입하면 타입 선택(step1)을 건너뛰고 step2부터 시작한다.
    const [step, setStep] = useState(initialType ? 2 : 1);
    const [requestType, setRequestType] = useState(initialType || LESSON_REQUEST_TYPES.REGULAR);

    const [options, setOptions] = useState({ tickets: [], brands: [] });
    const [locations, setLocations] = useState([]);
    const [products, setProducts] = useState([]);
    const [sports, setSports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [ticketId, setTicketId] = useState("");
    const [brandId, setBrandId] = useState("");
    const [locationId, setLocationId] = useState("");
    const [requestedDate, setRequestedDate] = useState(dayjs().add(1, "day").format("YYYY-MM-DD"));
    const [startTime, setStartTime] = useState("19:00");
    const [dayOrder, setDayOrder] = useState(1);
    const [participantCount, setParticipantCount] = useState(1);
    const [topic, setTopic] = useState("");
    // 체험(TRIAL) 종목 선택 — 선택한 종목 id는 sport_id, 이름은 topic 으로 매핑해 전송한다.
    const [trialSportId, setTrialSportId] = useState(null);
    const [requesterMessage, setRequesterMessage] = useState("");
    const [locationType, setLocationType] = useState("잠수풀");
    // 장소 검색 입력은 현재 비활성(주석) 상태라 setter는 두지 않는다. 재활성화 시 setLocationQuery 추가.
    const [locationQuery] = useState("");
    const [timeGroup, setTimeGroup] = useState("저녁");
    const didMountStepRef = useRef(false);

    useEffect(() => {
        if (!didMountStepRef.current) {
            didMountStepRef.current = true;
            return;
        }
        scrollToTop("auto");
    }, [step]);

    const meta = getRequestTypeMeta(requestType);
    // REGULAR/SPECIAL은 강습권 기반 요청 타입이다.
    // 두 타입 모두 사용자가 보유한 ticket_id가 반드시 있어야 요청할 수 있다.
    // REGULAR의 기존 상품 기반(no-ticket) 유료 요청 경로는 제거한다.
    const isTicketType = requestType === LESSON_REQUEST_TYPES.REGULAR || requestType === LESSON_REQUEST_TYPES.SPECIAL;
    const selectedTicket = options.tickets.find((ticket) => String(ticket.ticket_id || ticket.id) === String(ticketId));
    const hasTicket = isTicketType && !!ticketId;
    const showsTicketSection = hasTicket;
    const durationMinutes = getDefaultDurationMinutes(requestType);
    const timeSlotGroups = useMemo(
        () => TIME_SLOT_GROUPS.map((group) => ({
            ...group,
            times: group.times.filter((time) => fitsSameDayDuration(time, durationMinutes)),
        })).filter((group) => group.times.length > 0),
        [durationMinutes]
    );
    const endTime = addMinutes(startTime, durationMinutes);
    // const accentColor = getAccentColor(meta.accent);
    const selectedDate = dayjs(requestedDate);
    // const selectedTimeGroup = timeSlotGroups.find((group) => group.times.includes(startTime))?.label || timeGroup;
    const visibleTimes = timeSlotGroups.find((group) => group.label === timeGroup)?.times || timeSlotGroups[0]?.times || [];

    const filteredTickets = useMemo(() => {
        if (!isTicketType) return [];
        if (requestType === LESSON_REQUEST_TYPES.REGULAR) {
            return options.tickets.filter((ticket) => ticket.ticket_type === "NONE");
        }
        return options.tickets.filter((ticket) => ticket.ticket_type === SPECIAL_TRAINING_TICKET_TYPE);
    }, [options.tickets, requestType, isTicketType]);

    // 세션 회차 탭은 연결 상품의 sessions_count를 우선으로, 없으면 강습권 총 회차/이론 여부로 계산한다.
    const ticketLessonProduct = useMemo(
        () => products.find((product) => String(product.id) === String(selectedTicket?.lesson_product_id)),
        [products, selectedTicket]
    );
    const sessionSourceProduct = hasTicket ? ticketLessonProduct : null;
    const sessionSourceTicket = hasTicket ? selectedTicket : null;
    const sessionPlan = useMemo(() => {
        const totalSlots = getSessionSlotCount(sessionSourceProduct, sessionSourceTicket);
        const hasTheory = productHasTheory(sessionSourceProduct) || ticketHasTheory(sessionSourceTicket);
        const theoryDay = hasTheory
            ? (getProductTheoryDay(sessionSourceProduct) || getTicketTheoryDay(sessionSourceTicket))
            : null;
        const numericDays = Math.max(1, totalSlots - (hasTheory ? 1 : 0));
        return {
            theoryDay,
            dayOrders: [
                ...Array.from({ length: numericDays }, (_, index) => index + 1),
                ...(hasTheory ? ["theory"] : []),
            ],
        };
    }, [sessionSourceProduct, sessionSourceTicket]);
    const dayOrderOptions = sessionPlan.dayOrders;
    const theoryRequiredDay = sessionPlan.theoryDay;

    // 선수강(이론) 가드: theory_required_before_day 이상 회차는 이론 완료가 선행되어야 한다.
    const ticketTheoryCompleted = selectedTicket?.lesson_progress?.theory_completed;
    const selectedNumericDay = typeof dayOrder === "number" ? dayOrder : null;
    const selectedDayRequiresTheory =
        requestType === LESSON_REQUEST_TYPES.REGULAR &&
        hasTicket &&
        Number.isFinite(theoryRequiredDay) &&
        theoryRequiredDay > 0 &&
        selectedNumericDay !== null &&
        selectedNumericDay >= theoryRequiredDay;
    const prerequisiteNotMet = selectedDayRequiresTheory && ticketTheoryCompleted === false;
    const prerequisiteMessage = selectedNumericDay
        ? `${selectedNumericDay}강은 이론 수업을 먼저 완료해야 신청할 수 있어요.`
        : "이론 수업을 먼저 완료해야 신청할 수 있어요.";

    const visibleLocations = useMemo(() => {
      return locations
          .filter((location) =>  getLocationType(location) === locationType);
          
  }, [locations, locationType]);
  
    useEffect(() => {
        if (!userInfo) return undefined;
        let mounted = true;
        const load = async () => {
            setIsLoading(true);
            setLoadError(null);
            try {
                const [optionData, locationData, productData, sportData] = await Promise.all([
                    fetchLessonRequestOptions(),
                    getLocationList(),
                    getClassProduct(),
                    getSportsTypes(),
                ]);
                if (!mounted) return;
                setOptions({
                    tickets: normalizeArray(optionData?.tickets || [], "tickets"),
                    brands: normalizeArray(optionData?.brands || [], "brands"),
                });
                setLocations(normalizeArray(locationData, "locations"));
                setProducts(normalizeArray(productData, "results"));
                setSports(normalizeArray(sportData?.sports || sportData, "sports"));
            } catch (error) {
                if (!mounted) return;
                setLoadError(getLessonRequestErrorMessage(error));
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, [userInfo]);

    // 체험 종목 칩 — /buccl_main/api/v1/sports/ 조회 결과로 구성한다.
    // 선택 시 종목 id 는 sport_id, 이름은 topic 으로 매핑해 전송한다(handleSelectTrial 참고).
    // 실제 체험 상품은 BE 가 home brand 지정 상품으로 resolve 하며, 미설정 브랜드는 제출 시 400 으로 차단한다.
    const trialItems = useMemo(
        () => sports
            .map((sport) => ({ id: sport.id, label: sport.name }))
            .filter((item) => toPositiveInt(item.id) && item.label),
        [sports]
    );

    // PREMIUM 종목은 사용자가 다시 고르지 않는다. 홈 헤더가 관리하는 userInfo.preferred_sport 를
    // 제출 시점에 sport_id 로 자동 사용한다(아래 handleSubmit 참고).

    useEffect(() => {
        if (!isTicketType) {
            setTicketId("");
            return;
        }
        const hasSelectedTicket = filteredTickets.some(
            (ticket) => String(ticket.ticket_id || ticket.id) === String(ticketId)
        );
        if (filteredTickets[0] && !hasSelectedTicket) {
            setTicketId(String(filteredTickets[0].ticket_id || filteredTickets[0].id));
        } else if (!filteredTickets[0]) {
            setTicketId("");
        }
    }, [filteredTickets, isTicketType, ticketId]);


    useEffect(() => {
        if (brandId) return;
        // 홈에서 선택한 브랜드를 우선 사용한다. 없으면 기존 동작 유지를 위해 options.brands[0]로 폴백한다.
        if (homeBrandId) {
            setBrandId(String(homeBrandId));
            return;
        }
        if (options.brands[0]) setBrandId(String(options.brands[0].id));
    }, [brandId, homeBrandId, options.brands]);

    useEffect(() => {
        if (!locationId && locations[0]) setLocationId(String(locations[0].id));
    }, [locationId, locations]);

    useEffect(() => {
        if (visibleLocations.length === 0) return;
        const hasVisibleLocation = visibleLocations.some((location) => String(location.id) === String(locationId));
        if (!hasVisibleLocation) setLocationId(String(visibleLocations[0].id));
    }, [locationId, visibleLocations]);

    useEffect(() => {
        if (timeSlotGroups.length === 0) return;
        const selectedGroup = timeSlotGroups.find((group) => group.times.includes(startTime));
        if (selectedGroup) {
            if (timeGroup !== selectedGroup.label) setTimeGroup(selectedGroup.label);
            return;
        }
        const fallbackGroup = timeSlotGroups.find((group) => group.label === timeGroup) || timeSlotGroups[0];
        setTimeGroup(fallbackGroup.label);
        setStartTime(fallbackGroup.times[0]);
    }, [startTime, timeGroup, timeSlotGroups]);

    useEffect(() => {
        if (requestType !== LESSON_REQUEST_TYPES.REGULAR) return;
        if (!dayOrderOptions.includes(dayOrder)) setDayOrder(dayOrderOptions[0] ?? 1);
    }, [dayOrder, dayOrderOptions, requestType]);

    // --- Display items (step 컴포넌트는 표현만 담당) ---
    const ticketItems = useMemo(
        () => filteredTickets.map((ticket) => {
            const ticketProduct = products.find((product) => String(product.id) === String(ticket.lesson_product_id));
            return {
                id: String(ticket.ticket_id || ticket.id),
                label: getTicketLabel(ticket),
                sub: getTicketSessionsLabel(ticket, ticketProduct),
            };
        }),
        [filteredTickets, products]
    );

    const locationItems = useMemo(
        () => visibleLocations.map((location) => ({
            id: String(location.id),
            label: getLocationLabel(location),
            sub: formatLocationSub(location),
            image: getLocationImage(location),
        })),
        [visibleLocations]
    );
    const sessionTabs = useMemo(
        () => dayOrderOptions.map((order) => ({
            value: order,
            label: typeof order === "number" ? `${order}강` : "이론",
        })),
        [dayOrderOptions]
    );

    // step4(확인) 요약 — 사용자가 예약한 핵심 정보를 그대로 되짚어준다.
    const selectedLocation = locations.find((location) => String(location.id) === String(locationId));
    const confirmItems = useMemo(() => {
        const weekday = WEEKDAYS[selectedDate.day()];
        return [
            { label: "강습 타입", value: meta.title },
            { label: "예약 일시", value: `${selectedDate.format("YYYY.MM.DD")} (${weekday}) ${startTime} ~ ${endTime}` },
            { label: "예약 장소", value: selectedLocation ? getLocationLabel(selectedLocation) : "장소 미선택" },
            ...(topic.trim() && requestType === LESSON_REQUEST_TYPES.PREMIUM
                ? [{ label: "요청 메모", value: topic.trim() }]
                : []),
            ...(topic.trim() && requestType === LESSON_REQUEST_TYPES.SPECIAL
                ? [{ label: "스페셜 트레이닝 제목", value: topic.trim() }]
                : []),
            ...(topic.trim() && requestType === LESSON_REQUEST_TYPES.TRIAL
                ? [{ label: "체험 주제", value: topic.trim() }]
                : []),
            { label: "참여 인원", value: `${hasTicket ? 1 : Number(participantCount)}명` },
        ];
    }, [meta.title, selectedDate, startTime, endTime, selectedLocation, hasTicket, participantCount, requestType, topic]);

    // step2(일정) 검증 — 장소는 step3로 이동했으므로 제외한다.
    const validateSchedule = () => {
        if (!requestedDate) return "희망 날짜를 선택해 주세요.";
        if (!startTime) return "희망 시간을 선택해 주세요.";
        if (requestType === LESSON_REQUEST_TYPES.REGULAR && !hasTicket) return "사용 가능한 강습권이 필요해요. 먼저 강습권을 구매해 주세요.";
        if (requestType === LESSON_REQUEST_TYPES.SPECIAL && !hasTicket) return `${meta.shortTitle} 요청에는 사용할 스페셜 트레이닝권이 필요해요.`;
        // PREMIUM 종목(sport_id)은 userInfo.preferred_sport 로 자동 채우므로 사용자가 별도로 고르지 않는다.
        if (requestType === LESSON_REQUEST_TYPES.PREMIUM && !toPositiveInt(userInfo?.preferred_sport)) {
            return "현재 선택한 스포츠 정보가 필요해요. 홈에서 스포츠를 먼저 선택해 주세요.";
        }
        // TRIAL 은 sports 조회 칩에서 종목을 직접 골라야 sport_id 를 보낼 수 있다.
        if (requestType === LESSON_REQUEST_TYPES.TRIAL && !toPositiveInt(trialSportId)) {
            return "체험할 종목을 선택해 주세요.";
        }
        if (!hasTicket && !brandId) return "선택한 홈 브랜드 정보가 필요해요. 홈에서 브랜드를 먼저 선택해 주세요.";
        if (requestType === LESSON_REQUEST_TYPES.REGULAR && !dayOrder) return "회차를 선택해 주세요.";
        if (requestType === LESSON_REQUEST_TYPES.REGULAR && prerequisiteNotMet) return prerequisiteMessage;
        return null;
    };

    // step3(상세) 검증 — 장소 선택.
    const validateLocation = () => {
        if (!locationId) return "장소를 선택해 주세요.";
        return null;
    };

    // 최종 제출 검증 — 일정 + 장소 전체.
    const validate = () => validateLocation() || validateSchedule();

    const handleSelectTrial = (item) => {
        // 체험은 상품을 직접 고르지 않는다(BE 가 브랜드 지정 상품 resolve).
        // 선택한 종목 id 는 sport_id, 이름은 topic 으로 매핑한다. 같은 칩을 다시 누르면 선택 해제한다.
        const isSame = String(trialSportId) === String(item.id);
        setTrialSportId(isSame ? null : item.id);
        setTopic(isSame ? "" : item.label);
    };

    const handleNext = () => {
        if (step === 2) {
            const message = validateSchedule();
            if (message) {
                showError(message);
                return;
            }
        }
        if (step === 3) {
            const message = validateLocation();
            if (message) {
                showError(message);
                return;
            }
        }
        if (step < 4) {
            setStep(step + 1);
            return;
        }
        handleSubmit();
    };

    const handleBack = () => {
        // routeType으로 step 2에 바로 진입한 경우 step 1을 건너뛰었으므로 페이지를 벗어난다
        // if (step === 1 && initialType) {
        //     navigate(-1);
        //     return;
        // }
        if (step > 1) {
            setStep(step - 1);
        } else {
            navigate(-1);
        }
    };

    const handleSelectRequestType = (type) => {
        if (type === requestType) return;
        setRequestType(type);
        setTopic("");
        setTrialSportId(null);
    };

    const handleSubmit = async () => {
        const message = validate();
        if (message) {
            showError(message);
            return;
        }
        const payload = {
            request_type: requestType,
            location_id: Number(locationId),
            requested_date: requestedDate,
            requested_start_time: startTime,
            requested_end_time: endTime,
            participant_count: hasTicket ? 1 : Number(participantCount),
            requester_message: requesterMessage.trim(),
            topic: topic.trim(),
        };
        if (hasTicket) {
            payload.ticket_id = Number(ticketId);
        } else if (requestType === LESSON_REQUEST_TYPES.TRIAL) {
            // 체험: 상품 ID 전송 안 함 — BE 가 home brand 지정 체험 상품으로 resolve(미설정 시 400 차단).
            // 종목은 sports 조회 칩에서 고른 값을 sport_id(id) + topic(이름)으로 매핑해 보낸다.
            payload.brand_id = Number(homeBrandId || brandId);
            const trialSport = toPositiveInt(trialSportId);
            if (trialSport) payload.sport_id = trialSport;
        } else if (requestType === LESSON_REQUEST_TYPES.PREMIUM) {
            // 프리미엄: 상품 없이 브랜드 + 종목(sport_id)만 전송.
            // sport_id 는 사용자가 다시 고르지 않고 홈 헤더가 관리하는 preferred_sport 를 자동 사용한다.
            payload.brand_id = Number(homeBrandId || brandId);
            const preferredSport = toPositiveInt(userInfo?.preferred_sport);
            if (preferredSport) payload.sport_id = preferredSport;
        }
        if (requestType === LESSON_REQUEST_TYPES.REGULAR) {
            payload.requested_day_order = dayOrder === "theory" ? Number(theoryRequiredDay || 3) : Number(dayOrder);
            payload.requested_is_theory = dayOrder === "theory";
        }

        setIsSubmitting(true);
        try {
            await createLessonRequest(payload);
            showSuccess("수업 요청을 보냈어요. 강사 응답을 기다려 주세요.");
            navigate("/lesson-requests", { replace: true });
        } catch (error) {
            showError(getLessonRequestErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!userInfo) return <Navigate to="/login" replace />;


    // 강습권/스페셜 트레이닝권 기반 요청은 보유 티켓이 없으면 요청 생성을 막고 구매를 유도한다.
    const isRegularBlocked =
        requestType === LESSON_REQUEST_TYPES.REGULAR && step >= 2 && filteredTickets.length === 0;
    const isSpecialBlocked =
        requestType === LESSON_REQUEST_TYPES.SPECIAL && step >= 2 && filteredTickets.length === 0;
    const isTicketBlocked = isRegularBlocked || isSpecialBlocked;

    const showBottomBar = !isLoading && !loadError && !isTicketBlocked;

    return (
        <div className="lesson-request-page">
            <SubHeader title={"수업 요청"} customGoback={handleBack} />
            <div className="lesson-request-header__stepper">
                {[1, 2, 3, 4].map((s) => (
                    <div key={s} className={`step-segment ${s <= step ? "is-active" : ""}`} />
                ))}
            </div>
            {isLoading ? (
                <div className="lesson-request-state">
                    <LoadingSpinner />
                </div>
            ) : loadError ? (
                <div className="lesson-request-state">
                    <NoData title="수업 요청 정보를 불러오지 못했어요" desc={loadError} />
                    <button type="button" className="lesson-request-state__btn" onClick={() => window.location.reload()}>
                        다시 불러오기
                    </button>
                </div>
            ) : isTicketBlocked ? (
                <div className="lesson-request-state">
                    <NoData
                        title={isRegularBlocked ? "사용할 강습권이 없어요" : "사용할 스페셜 트레이닝권이 없어요"}
                        desc={isRegularBlocked
                            ? "강습권을 먼저 구매하면 보유한 티켓으로 원하는 날짜·회차 수업 개설을 요청할 수 있어요."
                            : "스페셜 트레이닝권을 먼저 구매하면 원하는 날짜·회차로 수업 개설을 요청할 수 있어요."}
                    />
                    <button type="button" className="lesson-request-state__btn" onClick={() => navigate("/class")}>
                        {isRegularBlocked ? "강습권 보러 가기" : "스페셜 트레이닝권 보러 가기"}
                    </button>
                </div>
            ) : (
                <main className="step-content">
                    {step === 1 && (
                        <Step1RequestType
                            onSelect={handleSelectRequestType}
                            selectedType={requestType}
                            onHistoryClick={() => navigate("/lesson-requests")}
                        />
                    )}
                    {step === 2 && (
                        <Step2Schedule
                            requestType={requestType}
                            meta={meta}
                            hasTicket={hasTicket}
                            showsTicketSection={showsTicketSection}
                            ticketItems={ticketItems}
                            ticketId={ticketId}
                            setTicketId={setTicketId}
                            trialItems={trialItems}
                            onSelectTrial={handleSelectTrial}
                            trialSportId={trialSportId}
                            topic={topic}
                            setTopic={setTopic}
                            sessionTabs={sessionTabs}
                            dayOrder={dayOrder}
                            setDayOrder={setDayOrder}
                            prerequisiteNotMet={prerequisiteNotMet}
                            prerequisiteMessage={prerequisiteMessage}
                            selectedDate={selectedDate}
                            setRequestedDate={setRequestedDate}
                            // timeSlotGroups={timeSlotGroups}
                            // selectedTimeGroup={selectedTimeGroup}
                            // setTimeGroup={setTimeGroup}
                            visibleTimes={visibleTimes}
                            startTime={startTime}
                            setStartTime={setStartTime}
                        />
                    )}
                    {step === 3 && (
                        <Step3Details
                            requestType={requestType}
                            participantCount={participantCount}
                            setParticipantCount={setParticipantCount}
                            message={requesterMessage}
                            setMessage={setRequesterMessage}
                            locationType={locationType}
                            setLocationType={setLocationType}
                            locationQuery={locationQuery}
                            locationItems={locationItems}
                            locationId={locationId}
                            setLocationId={setLocationId}
                        />
                    )}
                    {step === 4 && <Step4Confirm summaryItems={confirmItems} note={meta.note} />}
                </main>
            )}

            {showBottomBar && (
                <div className="step-bottom-bar">
                    <button
                        type="button"
                        className="btn btn-full btn-large btn-purple500"
                        onClick={handleNext}
                        disabled={isSubmitting}
                        
                    >
                        {isSubmitting ? "처리 중..." : step === 4 ? "요청 완료하기" : "다음"}
                    </button>
                </div>
            )}
        </div>
    );
}
